import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc, trpcErrorCode } from "../../../app/trpc.js";
import { Button, Card, Chip, Input, Label, Notice, Select, Spinner } from "../../../ui/index.js";
import { atLocal, dayKey, fmtDateTime, newKey, useCabinet } from "../../cabinet/index.js";
import { StepNav } from "../StepNav.js";
import type { StepProps } from "../types.js";
import { useUnsavedChanges } from "../unsaved.js";

interface Added {
  key: string;
  client: string;
  start: Date;
  master: string;
}

/**
 * Шаг 5: перенос будущих записей из тетрадки или старой программы, по одной, как ручная запись мастера
 * (`calendar.bookings.createManual`, клиент склеивается по телефону или @нику). Шаг можно пропустить.
 */
export function BookingsStep({ salonId, next, back }: StepProps) {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const services = useQuery(trpc.calendar.services.list.queryOptions({ salonId }));
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId }));
  const [client, setClient] = useState("");
  const [contact, setContact] = useState("");
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [masterId, setMasterId] = useState("");
  const [date, setDate] = useState(() => dayKey(new Date(), cab.timezone));
  const [time, setTime] = useState("10:00");
  const [key, setKey] = useState(newKey);
  const [added, setAdded] = useState<Added[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Начатая, но не добавленная запись: уже добавленные записи на сервере, их терять нечего.
  useUnsavedChanges(client.trim() !== "" || contact.trim() !== "" || serviceIds.length > 0);

  const create = useMutation(
    trpc.calendar.bookings.createManual.mutationOptions({
      onSuccess: (b) => {
        setAdded([
          ...added,
          { key, client: client.trim() || contact.trim(), start: b.period.start, master: masters.data?.find((m) => m.id === b.masterId)?.name ?? "" },
        ]);
        setClient("");
        setContact("");
        setServiceIds([]);
        setKey(newKey());
        setError(null);
        void queryClient.invalidateQueries(trpc.calendar.bookings.pathFilter());
      },
      onError: (e) => {
        const code = trpcErrorCode(e);
        setError(
          code === "slot_taken"
            ? t("onb.bookings.slotTaken")
            : code === "master_unavailable"
              ? t("onb.bookings.masterNoService")
              : code === "validation"
                ? t("onb.bookings.badContact")
                : t("common.error"),
        );
      },
    }),
  );

  if (services.isPending || masters.isPending) return <Spinner />;
  const master = masterId || masters.data?.[0]?.id || "";
  const onAdd = (e: FormEvent) => {
    e.preventDefault();
    if (!master || serviceIds.length === 0 || !/^\d{2}:\d{2}$/.test(time)) return;
    create.mutate({
      salonId,
      masterId: master,
      serviceIds,
      start: atLocal(date, time, cab.timezone),
      idempotencyKey: key,
      newClient: { name: client.trim() || null, phoneOrHandle: contact.trim() || null },
    });
  };
  return (
    <div>
      <p className="mb-4 text-slate-600">{t("onb.bookings.intro")}</p>
      <form onSubmit={onAdd}>
        <Card className="space-y-3">
          <div>
            <Label htmlFor="b-client">{t("onb.bookings.client")}</Label>
            <Input id="b-client" value={client} onChange={(e) => setClient(e.target.value)} maxLength={100} />
          </div>
          <div>
            <Label htmlFor="b-contact">{t("onb.bookings.contact")}</Label>
            <Input id="b-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="@marko · 064 123 4567" maxLength={50} />
          </div>
          <div>
            <Label>{t("onb.bookings.services")}</Label>
            <div className="flex flex-wrap gap-2">
              {(services.data ?? []).map((s) => (
                <Chip
                  key={s.id}
                  active={serviceIds.includes(s.id)}
                  onClick={() => setServiceIds(serviceIds.includes(s.id) ? serviceIds.filter((x) => x !== s.id) : [...serviceIds, s.id])}
                >
                  {s.name}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="b-master">{t("onb.bookings.master")}</Label>
            <Select id="b-master" value={master} onChange={(e) => setMasterId(e.target.value)}>
              {(masters.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="b-date">{t("onb.bookings.date")}</Label>
              <Input id="b-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="flex-1">
              <Label htmlFor="b-time">{t("onb.bookings.time")}</Label>
              <Input id="b-time" type="time" required step={300} value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          {error && <Notice kind="error">{error}</Notice>}
          <Button type="submit" variant="secondary" className="w-full" disabled={create.isPending || serviceIds.length === 0 || !master}>
            {t("onb.bookings.add")}
          </Button>
        </Card>
      </form>
      {added.length > 0 && (
        <Card className="mt-3 space-y-1">
          {added.map((a) => (
            <div key={a.key} className="text-sm">
              ✓ {fmtDateTime(a.start, cab.timezone, i18n.language)} · {a.master} · {a.client || t("onb.bookings.noName")}
            </div>
          ))}
        </Card>
      )}
      <StepNav onBack={back} onNext={next} nextLabel={added.length > 0 ? t("onb.next") : t("onb.bookings.nothing")} />
    </div>
  );
}
